import { CacheUtil } from "@base/util/Cache";
import { ImmHelper } from "@base/util/Immutable";
import { NormalizedRecordUtil } from "@base/util/NormalizedRecord";
import { getKeys, GetTaggedType, mapArrayToRecord, mapRecord } from "@base/util/TypeUtil";
import {
    ConfiguratorItemData, defaultDebugProperties, defaultSceneSettings, getItemRecordForItem, getItemRecordForItemType, ItemRecord, IVariantData3d, ProductData3d, productRecords, selectionTypeInfos, ShadowType
} from "@biz/definitions/IVariantData3d";
import { IModelData } from "@biz/definitions/ModelDefinitions";
import { IModelProductData } from "@biz/definitions/ModelProductDefinitions";
import { ISelectionTypeId, SelectionData, SelectionType } from "@biz/definitions/SelectionDefinitions";
import { ISunSailData, RopeColors } from "@biz/definitions/SunSailDefinitions";
import { IRaffData } from "@biz/items/raff/RaffDefinitions";
import { SelectionHelper } from "@biz/items/reducers/ConfiguratorSlice.reducer";
import { GroupHelper } from "@biz/logic/GroupHelper";
import { ShoppingList } from "@biz/models/ShoppingList";

export interface IItemTreeInfo {
  id: string;
  parentId: string | undefined;
  itemType: ConfiguratorItemData["itemType"]
}

const selectionTypes: SelectionType[] = [];
{
  const keys = getKeys(selectionTypeInfos);
  for (const k of keys) {
    selectionTypes.push(k);
  }
}

const recordsArray: ItemRecord[] = [];
{
  for (const k of selectionTypes) {
    const record = getItemRecordForItemType(k);
    if (record)
      recordsArray.push(record)
  }
}

export class VariantData3d {
  static create(): IVariantData3d {

    const records: Pick<IVariantData3d, ItemRecord> = {} as any;
    for (const k of selectionTypes) {
      const record = getItemRecordForItemType(k);
      if (record)
        records[record] = NormalizedRecordUtil.createEmpty()
    }
    return {
      type: "sail3d",
      ...records,
      layers: {},
      userFiles: {},
      debugFlags: defaultDebugProperties,
      settings: defaultSceneSettings,
      selection: [],
      shoppingList: ShoppingList.createEmpty(),
      sideTab: "properties"
    }
  }

  static getRecords(): ItemRecord[] {
    return recordsArray;
  }

  static getProductRecords(): ItemRecord[] {
    return productRecords;
  }

  static getProductsArray(variant: IVariantData3d, options: { orderableOnly: boolean }): ProductData3d[] {
    const products: ProductData3d[] = [];
    const records = VariantData3d.getProductRecords();
    for (const r of records) {
      if (r !== "items") {
        const obj = variant[r]?.byId;
        if (obj) {
          const items = Object.values(obj)
          for (const item of items) {
            if (options.orderableOnly) {
              const notOrderable = !GroupHelper.isOrderable(variant, item);
              if (notOrderable)
                continue;
            }
            products.push(item);
          }
        }
      }
    }
    return products;
  }


  static getProductsAsRecord(item: IVariantData3d, options: { orderableOnly: boolean }): Record<string, ProductData3d> {
    const products = this.getProductsArray(item, options);
    return mapArrayToRecord(products, (p) => p.id)
  }

  static findSelectionWithId(s: IVariantData3d, id: string | undefined): SelectionData | undefined {
    if (!id)
      return undefined;

    const selections = SelectionHelper.toSelectionArray(s.selection);
    for (let i = 0; i < selections.length; i++) {
      if (selections[i].id === id)
        return selections[i];
    }
    return undefined;
  }

  static getSailOrRaff(s: IVariantData3d, id: string): ISunSailData | IRaffData {
    const ss = s.sunSails.byId[id];
    if (ss)
      return ss;
    return s.raffs.byId[id];
  }

  static getProduct(s: IVariantData3d, id: string): ProductData3d | undefined {
    const records = VariantData3d.getProductRecords();
    for (const r of records) {
      const item = s[r]?.byId[id];
      if (item)
        return item as ProductData3d;
    }
    return undefined;
  }

  static getTyped<T extends ConfiguratorItemData["itemType"]>(type: T, s: IVariantData3d, id: string): GetTaggedType<ConfiguratorItemData, { itemType: T }> | undefined {
    if (s.type !== "sail3d")
      throw new Error("Invalid variant data type " + s.type);
    const record = getItemRecordForItemType(type)
    if (record) {
      return s[record].byId[id] as GetTaggedType<ConfiguratorItemData, { itemType: T }> | undefined;
    }
    return undefined;
  }

  static getModelOrModelProduct(s: IVariantData3d, id: string): IModelData | IModelProductData | undefined {
    const ss = VariantData3d.getTyped("model", s, id) || VariantData3d.getTyped("modelProduct", s, id);
    return ss;
  }

  static prependItemIndexString(itemName: string | undefined, index: number): string {
    const pre = (index + 1) + "/ ";
    return pre + itemName;
  }

  static findVariantData3dItem(data: IVariantData3d, id: string | undefined): ConfiguratorItemData | undefined {
    if (!id) return undefined;
    const items = VariantData3d.calcVariantData3dItems(data);
    return items[id];
  }

  static findVariantData3dItemAndIndex(data: IVariantData3d, id: string): { item: ConfiguratorItemData; index?: number } | undefined {
    const items = VariantData3d.calcVariantData3dItemsWithIndex(data);
    const val = items[id];
    return val;
  }

  static _calcVariantData3dItems(data: IVariantData3d): Record<string, ConfiguratorItemData> {
    const ret: Record<string, ConfiguratorItemData> = {};
    const records = VariantData3d.getRecords();
    for (const r of records) {
      const recordData = data[r];
      if (recordData?.ids) {
        for (const id of recordData.ids) {
          const item = recordData.byId[id];
          ret[id] = item;
        }
      }
    }
    return ret;
  }

  static calcVariantData3dItems = CacheUtil.memo(VariantData3d._calcVariantData3dItems, "VariantData3d._calcVariantData3dItems", {
    idsFromArgs: ["data"],
    inputsEqualFn: "ref",
    resultEqualFn: "level2",
  })

  static _calcVariantData3dItemTreeInfo(data: IVariantData3d): Record<string, IItemTreeInfo> {
    const ret: Record<string, IItemTreeInfo> = {};
    const records = VariantData3d.getRecords();
    for (const r of records) {
      const recordData = data[r];
      if (recordData?.ids) {
        for (const id of recordData.ids) {
          const item = recordData.byId[id];
          ret[id] = { id: item.id, parentId: item.parentId, itemType: item.itemType }
        }
      }
    }
    return ret;
  }

  static calcVariantData3dItemTreeInfo = CacheUtil.memo(VariantData3d._calcVariantData3dItemTreeInfo, "VariantData3d._calcVariantData3dItemTreeInfo", {
    idsFromArgs: ["data"],
    inputsEqualFn: "ref",
    resultEqualFn: "level2",
  })

  static _calcVariantData3dItemsWithIndex(data: IVariantData3d): Record<string, { item: ConfiguratorItemData; index?: number }> {
    const items = VariantData3d.calcVariantData3dItems(data);
    let index = 0;

    return mapRecord(items, (item) => {
      const notOrderable = !GroupHelper.isOrderable(data, item);
      if (notOrderable)
        return { item };
      else {
        index++;
        return {
          item, index: index - 1
        }
      }
    })
  }

  static calcVariantData3dItemsWithIndex = CacheUtil.memo(VariantData3d._calcVariantData3dItemsWithIndex, "VariantData3d._calcVariantData3dItemsWithIndex", {
    idsFromArgs: ["data"],
    inputsEqualFn: "ref",
    resultEqualFn: "level3",
  })



  /** @deprecated */
  static _calcSelectionTypeIds(s: IVariantData3d): ISelectionTypeId[] {
    return selectionTypes.map((k) => ({ selectionType: k, ids: s[selectionTypeInfos[k].data]?.ids || [] }))
  }

  /** @deprecated */
  static calcSelectionTypeIds = CacheUtil.memo(VariantData3d._calcSelectionTypeIds, "VariantData3d.calcSelectionTypeIds", {
    inputsEqualFn: "ref",
    resultEqualFn: "level2",
  })


  // static notFoundLayerData: ILayerData = { hidden: true, locked: "locked", notOrderable: true }
  // static _calcMergedLayerState(s: IVariantData3d, id: string, layerKey?: SelectionType): ILayerData {
  //   let item: ConfiguratorItemData | undefined;
  //   if (!layerKey) {
  //     item = VariantData3d.findVariantData3dItem(s, id);
  //   } else {
  //     const key = getItemRecordForItemType(layerKey);
  //     if (key)
  //       item = s[key]?.byId?.[id];
  //   }

  //   if (!item)
  //     return VariantData3d.notFoundLayerData;

  //   return VariantData3d.calcMergedLayerStateForItem(item, s.layers);
  // }

  /** @deprecated */
  static getFirstSelection(s: IVariantData3d): SelectionData | undefined {
    const selections = VariantData3d.getSelectionArray(s.selection);
    if (selections.length)
      return selections[0];
    return undefined;
  }

  static findSelection(s: IVariantData3d, id: string, options?: { only?: boolean }): {
    item: SelectionData | undefined,
    selectionType: "main" | "secondary" | undefined,
    count: number
  } | undefined {
    const selections = VariantData3d.getSelectionArray(s.selection);
    if (options?.only && selections.length > 1) {
      return undefined;
    }
    const index = selections.findIndex((item) => item.id === id);
    if (index === - 1)
      return undefined;
    return {
      item: selections[index],
      selectionType: index === 0 ? "main" : "secondary",
      count: selections.length
    }
  }

  static getSelectionCount(s: IVariantData3d): number {
    if (Array.isArray(s.selection))
      return s.selection.length;
    else if (s.selection)
      return 1;
    return 0;
  }

  static findTypedSelection<ST extends SelectionType>(type: ST, s: IVariantData3d, id: string, options?: { only?: boolean }): {
    item: GetTaggedType<SelectionData, { type: ST }>,
    selectionType: "main" | "secondary" | undefined,
    count: number
  } | undefined {
    const selection = this.findSelection(s, id, options);
    if (selection?.item?.type === type) {
      return selection as any;
    }
    if (selection) {
      throw new Error("Selection found but different type " + selection?.item?.type + " vs " + type)
    }
    return undefined;
  }

  static isSelected(s: IVariantData3d, id: string): boolean {
    return !!VariantData3d.findSelection(s, id);
  }

  static getSelectionArray(selection: IVariantData3d["selection"]): SelectionData[] {
    if (!selection)
      return [];
    return Array.isArray(selection) ? selection : [selection];
  }

  static getSelected<T extends SelectionData>(s: IVariantData3d, target: T, options?: { strict: boolean, not?: Partial<SelectionData> }): T | undefined {
    const selections = VariantData3d.getSelectionArray(s.selection);
    for (const selection of selections) {
      const item = VariantData3d.#getSelectedItem(selection, target, options);
      if (item)
        return item;

    }
    return undefined;
  }

  static #getSelectedItem<T extends SelectionData>(selection: SelectionData | undefined, target: T, options?: { strict: boolean, not?: Partial<SelectionData> }): T | undefined {
    const { strict, not } = options || {};
    if (!selection)
      return undefined;

    if (strict) {
      const sourceKeys = getKeys(selection);
      for (const k of sourceKeys) {
        if (selection[k] !== target[k])
          return undefined;
      }
    }

    const targetKeys = getKeys(target);
    for (const k of targetKeys) {
      if ((selection as any)[k] !== target[k])
        return undefined;
    }
    if (not) {
      const notKeys = getKeys(not);
      for (const k of notKeys) {
        if (selection[k] === not[k])
          return undefined;
      }
    }
    return selection as (T | undefined);
  }

  static replaceSelection(old: IVariantData3d, selectionToReplace: SelectionData | undefined, newSelection: SelectionData): IVariantData3d {
    if (!selectionToReplace)
      return old;

    const newSel: IVariantData3d["selection"] = [
      ...VariantData3d.getSelectionArray(old.selection).map((item) => {
        if (item === selectionToReplace)
          return newSelection
        return item;
      })
    ];
    return ImmHelper.setProperty(old, "selection", newSel);
  }

  static initialRopeColor: string | undefined;
  static getRopeColorSelected(s: IVariantData3d, defaultColor: string = RopeColors.Selected): string {
    if (!VariantData3d.initialRopeColor)
      VariantData3d.initialRopeColor = s.testSettings?.selectedRopeColor;
    if (VariantData3d.initialRopeColor !== s.testSettings?.selectedRopeColor)
      return s.testSettings?.selectedRopeColor || defaultColor;
    return defaultColor;
  }

  static updateConfiguratorItem(old: IVariantData3d, id: string, updateFn: (item: ConfiguratorItemData) => ConfiguratorItemData): IVariantData3d {
    const item = VariantData3d.findVariantData3dItem(old, id);
    if (item) {
      const key = getItemRecordForItem(item);
      if (key) {
        const oldItem = old[key]?.byId?.[item.id];
        if (oldItem) {
          const newItem = updateFn(oldItem);
          return ImmHelper.setProperty(old, key, "byId", item.id, newItem);
        }
      }
    }
    return old;
  }
  static getShadowType(): ShadowType {
    return "pcf"
  }
}
